/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { getGiftCardAsync, getTenderTypesAsync, resolveCardTypesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';
import {
    CardType, CardTypeInfo, GiftCard, RetailOperation, TenderType
} from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classname from 'classnames';
import * as React from 'react';

import { OPERATIONS } from '../checkout';
import { getForm, IForm } from './components/get-form';
import { getItem, IItem } from './components/get-item';
import { IGiftCardBalanceCheckProps } from './gift-card-balance-check.props.autogenerated';

interface IGiftCardBalanceCheckState {
    isFetchingGiftCard: boolean;
    errorMessage: string;
    giftCardNumber: string;
    giftCardPin: string;
    giftCardExp: string;
    giftCard?: GiftCard;
}

enum SupportedGiftCardType {
    Internal = 'internal',
    External = 'external',
    Both = 'both'
}

export interface IGiftCardBalanceCheckModuleProps extends IGiftCardBalanceCheckProps<{}> { }

export interface IGiftCardBalanceCheckViewProps extends IGiftCardBalanceCheckProps<{}>, IGiftCardBalanceCheckState {
    className?: string;

    form: IForm;
    item?: IItem;

    giftCardBalanceCheckProps: IModuleProps;

    enterGiftCardNumber?(giftCardNumber: string): void;
    enterGiftCardPin?(giftCardPin: string): void;
    enterGiftCardExp?(giftCardExp: string): void;
    applyGiftCard?(): void;
}

/**
 *
 * GiftCardBalanceCheck component.
 * @extends {React.Component<IGiftCardBalanceCheckModuleProps, IGiftCardBalanceCheckState>}
 */
export class GiftCardBalanceCheck extends React.Component<IGiftCardBalanceCheckModuleProps, IGiftCardBalanceCheckState> {
    public state: IGiftCardBalanceCheckState = {
        isFetchingGiftCard: false,
        errorMessage: '',
        giftCardNumber: '',
        giftCardPin: '',
        giftCardExp: ''
    };

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    private readonly inputPinRef: React.RefObject<HTMLInputElement> = React.createRef();

    private readonly inputExpRef: React.RefObject<HTMLInputElement> = React.createRef();

    public render(): JSX.Element | null {
        const {
            config: { className, showAdditionalFields },
            resources
        } = this.props;
        const { errorMessage, giftCardNumber, giftCardPin, giftCardExp } = this.state;

        const supportedGiftCardType = this.props.context.app.config.giftCardSupported;

        if (!this._isEnabled()) {
            this.props.context.telemetry.error('Gift card balance check is not supported, module wont render');
            return null;
        }

        const supportExternalGiftCard = supportedGiftCardType !== SupportedGiftCardType.Internal;

        const moduleClassName = classname('ms-gift-card-balance-check', className);

        const viewProps: IGiftCardBalanceCheckViewProps = {
            ...this.props,
            ...this.state,
            className: moduleClassName,
            giftCardBalanceCheckProps: { moduleProps: this.props, className: moduleClassName },

            enterGiftCardNumber: this._enterGiftCardNumber,
            enterGiftCardPin: this._enterGiftCardPin,
            enterGiftCardExp: this._enterGiftCardExp,
            applyGiftCard: this._applyGiftCard,

            form: getForm({
                errorMessage,
                giftCardNumber,
                giftCardPin,
                giftCardExp,
                inputRef: this.inputRef,
                inputPinRef: this.inputPinRef,
                inputExpRef: this.inputExpRef,
                resources,
                supportExternalGiftCard,
                additionalFields: showAdditionalFields,
                onEnterGiftCardNumber: this._enterGiftCardNumber,
                onEnterGiftCardPin: this._enterGiftCardPin,
                onEnterGiftCardExp: this._enterGiftCardExp,
                onApplyGiftCard: this._applyGiftCard
            }),
            item: this.state.giftCard && getItem({
                giftCard: this.state.giftCard,
                getFormattedPrice: this._getFormattedPrice,
                resources
            })
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    private readonly _isEnabled = (): boolean => {
        if (!this.props.context.request.user.isAuthenticated && this.props.context.app.config.giftCardSupported !== SupportedGiftCardType.External) {
            return false;
        }
        return true;
    };

    private readonly _getFormattedPrice = (price: number = 0, currencyCode: string = 'USD'): string => {
        return this.props.context.cultureFormatter.formatCurrency(price, currencyCode);
    };

    private readonly _enterGiftCardNumber = (giftCardNumber: string): void => {
        this.setState({
            giftCardNumber
        });
        this._clearError();
    };

    private readonly _enterGiftCardPin = (giftCardPin: string): void => {
        this.setState({
            giftCardPin
        });
        this._clearError();
    };

    private readonly _enterGiftCardExp = (giftCardExp: string): void => {
        this.setState({
            giftCardExp
        });
        this._clearError();
    };

    private readonly _clearError = (): void => {
        this.setState({
            errorMessage: ''
        });
    };

    private readonly _setError = (errorMessage: string): void => {
        this.props.telemetry.error('Error', errorMessage);
        this.props.telemetry.debug('Error', errorMessage);
        this.setState({
            errorMessage
        });
    };

    private readonly _applyGiftCard = async (): Promise<void> => {
        if (this.state.isFetchingGiftCard) {
            return;
        }
        this.setState({
            isFetchingGiftCard: true
        });

        const giftCardNumber = this.state.giftCardNumber.trim();
        const giftCardPin = this.state.giftCardPin.trim();
        const giftCardExp = this.state.giftCardExp.trim();

        const giftCardTypes = await this._getGiftCardTypes(giftCardNumber);
        const giftCardType = giftCardTypes && giftCardTypes[0];
        const isPinRequired = giftCardType && giftCardType.IsPinRequired;
        const isExpRequired = giftCardType && giftCardType.IsExpirationDateRequired;
        const tenderTypeId = giftCardType && giftCardType.PaymentMethodId;
        const giftCard = await this._getGiftCard(giftCardNumber, giftCardPin, giftCardExp, isPinRequired, isExpRequired, tenderTypeId);

        if (giftCard) {
            this._clearError();
            this.setState({
                giftCardNumber: '',
                giftCardPin: '',
                giftCardExp: '',
                isFetchingGiftCard: false,
                giftCard
            });
            return Promise.resolve();
        }
        const input =
                this.inputRef && this.inputRef.current && this.inputRef.current.focus && (this.inputRef.current as HTMLElement);
        input && input.focus();
        this.setState({
            isFetchingGiftCard: false,
            giftCard: undefined
        });

    };

    private readonly _findGiftcardTenderTypes = (tenderTypes: TenderType[], operationId: RetailOperation, giftcardType: string): string[] | undefined => {
        let matchedTenderTypes: TenderType[] | undefined;

        switch (giftcardType) {
            case SupportedGiftCardType.Internal:
                matchedTenderTypes = tenderTypes.filter(tenderType => (tenderType.OperationId === operationId && tenderType.ConnectorId === ''));
                break;
            case SupportedGiftCardType.External:
                matchedTenderTypes = tenderTypes.filter(tenderType => (tenderType.OperationId === operationId && tenderType.ConnectorId !== ''));
                break;
            default:
                throw new Error('Invalid gift card type');
        }

        if (matchedTenderTypes) {
            return matchedTenderTypes.map(tenderType => tenderType.TenderTypeId || '');
        }
        return;
    };

    private readonly _getGiftCard = async (giftCardNumber: string, giftCardPin: string, giftCardExp: string, isPinRequired: boolean | undefined, isExpRequired: boolean | undefined, tenderTypeId: string | undefined): Promise<GiftCard | undefined> => {
        const {
            resources: { invalidCardTypeError, noBalanceError, invalidCardInfoError, noCardPinError, noCardExpError }
        } = this.props;

        const supportedGiftCardType = this.props.context.app.config.giftCardSupported;

        if (!tenderTypeId) {
            return undefined;
        }

        const tenderTypes = await getTenderTypesAsync({ callerContext: this.props.context.actionContext, queryResultSettings: {} }).catch(error => {
            throw error;
        });

        if (!tenderTypes) {
            throw new Error('Fail to get gift card tender line');
        }

        const internalGiftcardTenderTypes = this._findGiftcardTenderTypes(tenderTypes, OPERATIONS.PayGiftCertificate, SupportedGiftCardType.Internal);
        const externalGiftcardTenderTypes = this._findGiftcardTenderTypes(tenderTypes, OPERATIONS.PayGiftCertificate, SupportedGiftCardType.External);

        switch (supportedGiftCardType) {
            case undefined:
            case SupportedGiftCardType.Internal:
                if (!internalGiftcardTenderTypes?.includes(tenderTypeId)) {
                    this._setError(invalidCardTypeError);
                    return undefined;
                }
                break;
            case SupportedGiftCardType.External:
                if (!externalGiftcardTenderTypes?.includes(tenderTypeId)) {
                    this._setError(invalidCardTypeError);
                    return undefined;
                }
                break;
            case SupportedGiftCardType.Both:
                if (!internalGiftcardTenderTypes?.includes(tenderTypeId) && !externalGiftcardTenderTypes?.includes(tenderTypeId)) {
                    this._setError(invalidCardTypeError);
                    return undefined;
                }
                break;
            default:
                throw new Error('Unsupported gift card type');
        }

        if (!this.props.context.request.user.isAuthenticated && internalGiftcardTenderTypes?.includes(tenderTypeId)) {
            this._setError(invalidCardTypeError);
            return undefined;
        }

        if (isPinRequired && giftCardPin === '') {
            this._setError(noCardPinError);
            return undefined;
        }

        if (isExpRequired && giftCardExp === '') {
            this._setError(noCardExpError);
            return undefined;
        }

        const month = Number.parseInt(giftCardExp.split('/')[0], 10);
        const year = Number.parseInt(giftCardExp.split('/')[1], 10);

        return getGiftCardAsync({ callerContext: this.props.context.actionContext }, giftCardNumber, tenderTypeId, giftCardPin, month, year)
            .then(activeGiftCard => {
                if (!activeGiftCard.Balance || activeGiftCard.Balance === 0) {
                    this._setError(noBalanceError);
                    return;
                }
                return activeGiftCard;
            })
            .catch(error => {
                this._setError(invalidCardInfoError);
                return undefined;
            });
    };

    private readonly _getGiftCardTypes = async (giftCardNumber: string): Promise<CardTypeInfo[] | undefined> => {
        const {
            resources: { emptyInputError, invalidCodeError }
        } = this.props;

        if (!giftCardNumber) {
            this._setError(emptyInputError);
            return undefined;
        }

        const GIFTCARDTYPE = 7 as CardType.GiftCard; // This is a workaround. ts-jest doesn't support 'const enum'
        return resolveCardTypesAsync({ callerContext: this.props.context.actionContext }, giftCardNumber, GIFTCARDTYPE)
            .then(giftCardTypes => {
                if (!giftCardTypes || giftCardTypes.length === 0 || giftCardTypes[0] === undefined) {
                    this._setError(invalidCodeError);
                    return;
                }
                return giftCardTypes;
            })
            .catch(error => {
                this._setError(invalidCodeError);
                return undefined;
            });
    };
}

export default GiftCardBalanceCheck;
